import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertDialog } from 'common/components/AlertDialog';
import { UserImportForm } from 'user/forms';
import { IImportData, EUsersImportType } from 'user/types';

import { UserImportTaskStatus } from './UserImportTaskStatus';

interface IProps {
    importData: IImportData | null;
    importType: EUsersImportType;
    forRelatedIds?: string[];
    showTrainingEmail?: boolean;
    open: boolean;
    onClose(): void;
    onSuccess?(succeededCount: number): void;
}

interface ICachedData {
    importData: IImportData;
    importType: EUsersImportType;
    forRelatedIds?: string[];
    showTrainingEmail?: boolean;
}

export const UserImportModal = ({
    importData,
    importType,
    forRelatedIds,
    showTrainingEmail,
    open,
    onClose,
    onSuccess,
}: IProps) => {
    const [translate] = useTranslation();
    const [watchingTaskId, setWatchingTaskId] = useState<string | undefined>();
    const [cachedData, setCachedData] = useState<ICachedData | null>(null);

    // Cache props when dialog opens so content persists through close transition
    useEffect(() => {
        if (open && importData) {
            setCachedData({
                importData,
                importType,
                forRelatedIds,
                showTrainingEmail,
            });
        }
    }, [open, importData, importType, forRelatedIds, showTrainingEmail]);

    const handleExited = useCallback(() => {
        setWatchingTaskId(undefined);
        setCachedData(null);
    }, []);

    return (
        <AlertDialog
            maxWidth={false}
            open={open}
            slotProps={{
                paper: { sx: { width: '100%', maxWidth: '768px' } },
                transition: { onExited: handleExited },
            }}
            title={translate('userImportModal.title', {
                type: translate(
                    `userImportModal.type.${cachedData?.importType ?? importType}`
                ).toLowerCase(),
            })}
            onClose={onClose}
        >
            {cachedData && (
                <>
                    {watchingTaskId ? (
                        <UserImportTaskStatus
                            taskId={watchingTaskId}
                            onClose={onClose}
                            onRetry={() => setWatchingTaskId(undefined)}
                            onSuccess={onSuccess}
                        />
                    ) : (
                        <UserImportForm
                            forRelatedIds={cachedData.forRelatedIds}
                            importData={cachedData.importData}
                            importType={cachedData.importType}
                            showTrainingEmail={cachedData.showTrainingEmail}
                            onClose={onClose}
                            onSubmitSuccess={setWatchingTaskId}
                        />
                    )}
                </>
            )}
        </AlertDialog>
    );
};
